import { useMemo } from 'react'
import { Link, useLocation, useOutletContext, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowLeft01Icon,
  ComputerIcon,
  Cursor01Icon,
  GlobalIcon,
  Link01Icon,
  Note01Icon,
  SmartPhone01Icon,
  ViewIcon,
} from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/layout'
import Favicon from '@/components/shared/Favicon'
import { ShellHeaderActions, ShellHeaderCrumb } from '@/components/layout/ShellHeader'
import { shellControlClass } from '@/components/layout/shellControl'
import { VisitorOrb } from '@/components/dashboard/VisitorOrb'
import { agoLabel, countryName, useNow } from '@/components/dashboard/VisitorProfileSheet'
import { clickedPhrase } from '@/components/heatmaps/heatmapInsights'
import { useLiveVisitorFeed } from '@/hooks/useLiveVisitorFeed'
import {
  activityKind,
  formatVisitDuration,
  isOnSite,
  timeOnPage,
  visitorName,
  type RealtimeActivity,
  type VisitorProfile as Profile,
} from '@/lib/realtime/visitorProfiles'

interface DashboardContext {
  token?: string
  propertyId?: string
  isAggregateMode?: boolean
  enabledProperties?: Array<{ propertyId: string }>
}

// Flush to the white panel like the Paper frame (same bleed as Heatmaps).
const BLEED = '-mx-4 -mt-5 -mb-8 sm:-mx-9 sm:-mt-7 sm:-mb-9 flex min-h-[calc(100svh-68px)] flex-col md:flex-row'

const ACTIVITY_MINUTES = 60
const ACTIVITY_ROWS = 5

const clock = (time: string) => new Date(time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

function dayLabel(time: string, now: number): string {
  const d = new Date(time)
  if (d.toDateString() === new Date(now).toDateString()) return 'Today'
  if (d.toDateString() === new Date(now - 86_400_000).toDateString()) return 'Yesterday'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** While the visitor is on site the visit runs to now, not to their last event. */
const visitSeconds = (p: Profile, now: number) =>
  isOnSite(p, now) ? Math.max(p.durationSeconds, Math.round((now - new Date(p.firstSeen).getTime()) / 1000)) : p.durationSeconds

const isMobile = (p: Profile) => /ios|android|mobile|ipad|tablet/i.test(`${p.device ?? ''} ${p.deviceGroup ?? ''}`)

/** "google" → google.com; a referrer host stays as it is. */
const sourceDomain = (source: string) => {
  const s = source.trim().toLowerCase()
  return s.includes('.') ? s.replace(/^https?:\/\//, '').split('/')[0] : `${s.replace(/\s+/g, '')}.com`
}

function SourceMark({ source, size = 12 }: { source: string | null; size?: number }) {
  if (!source) return <HugeiconsIcon icon={Link01Icon} size={size} className="shrink-0 text-muted-foreground" />
  return (
    <Favicon
      domain={sourceDomain(source)}
      size={size}
      renderFallback={() => <HugeiconsIcon icon={GlobalIcon} size={size} className="shrink-0 text-muted-foreground" />}
    />
  )
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex h-[22px] items-center gap-[5px] rounded-md bg-card px-2 text-xs font-medium leading-4 text-muted-foreground shadow-[0_0_0_1px_rgb(0_0_0/0.08)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
      {children}
    </span>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-1 basis-0 flex-col gap-1">
      <span className="text-xs leading-4 text-foreground/60">{label}</span>
      <span className="text-base font-semibold leading-5 text-ui-title tabular-nums">{value}</span>
    </div>
  )
}

/** Minute-by-minute dots for the last hour: grey when the visitor did something, ink for the latest. */
function ActivityDots({ profile, now }: { profile: Profile; now: number }) {
  const { active, latest } = useMemo(() => {
    const active = new Set<number>()
    for (const e of profile.events) {
      const ago = Math.floor((now - new Date(e.time).getTime()) / 60_000)
      if (ago >= 0 && ago < ACTIVITY_MINUTES) active.add(ACTIVITY_MINUTES - 1 - ago)
    }
    return { active, latest: active.size ? Math.max(...active) : -1 }
  }, [profile.events, now])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-[3px]">
        <h2 className="text-sm font-semibold leading-[18px] text-ui-title">Activity</h2>
        <p className="text-xs leading-4 text-foreground/60">
          Active in {active.size} of the last {ACTIVITY_MINUTES} minutes
        </p>
      </div>
      <div
        role="img"
        aria-label={`Active in ${active.size} of the last ${ACTIVITY_MINUTES} minutes`}
        className="grid w-fit grid-flow-col gap-[5px]"
        style={{ gridTemplateRows: `repeat(${ACTIVITY_ROWS}, 9px)` }}
      >
        {Array.from({ length: ACTIVITY_MINUTES }, (_, i) => (
          <span
            key={i}
            className={cn(
              'size-[9px] rounded-full',
              i === latest ? 'bg-foreground' : active.has(i) ? 'bg-foreground/35' : 'ring-1 ring-inset ring-foreground/[0.13]',
            )}
          />
        ))}
      </div>
    </div>
  )
}

function EventRow({ event, events, index, live }: { event: RealtimeActivity; events: RealtimeActivity[]; index: number; live: boolean }) {
  const kind = activityKind(event)
  const page = event.page || '/'
  let icon = ViewIcon
  let label = page
  let meta: string | null
  if (kind === 'view') {
    const seconds = timeOnPage(events, index)
    meta = seconds != null ? formatVisitDuration(seconds) : live ? 'Reading now' : null
  } else if (kind === 'click') {
    icon = Cursor01Icon
    label = clickedPhrase(event.target, event.secondary)
    meta = `on ${page}`
  } else {
    icon = Note01Icon
    label = 'Submitted a form'
    meta = `on ${page}`
  }
  return (
    <li className="flex h-10 shrink-0 items-center gap-2.5 rounded-lg bg-canvas px-3">
      <HugeiconsIcon icon={icon} size={15} className="shrink-0 text-foreground/45" />
      <span className="min-w-0 flex-1 truncate text-sm leading-[18px] text-ui-title">{label}</span>
      {meta && <span className="shrink-0 text-xs leading-4 text-foreground/60 tabular-nums">{meta}</span>}
    </li>
  )
}

function VisitCard({ profile, now }: { profile: Profile; now: number }) {
  const live = isOnSite(profile, now)
  // Oldest-first indices for time-on-page, rendered newest first like the design.
  const rows = profile.events.map((event, index) => ({ event, index })).reverse()
  const sourceLabel = profile.source
    ? `${profile.source}${profile.medium ? ` · ${profile.medium}` : ''}`
    : 'Direct or unknown'
  return (
    <section className="surface-raised flex w-full max-w-[560px] flex-col rounded-xl bg-card">
      <div className="flex items-center gap-2 px-4 pb-2 pt-3">
        {live && <span className="size-1.5 shrink-0 rounded-full bg-live" aria-hidden />}
        <span className="text-xs leading-4 text-foreground/60 tabular-nums">
          {live
            ? `Now · ${formatVisitDuration(visitSeconds(profile, now))} so far`
            : `Visit lasted ${formatVisitDuration(profile.durationSeconds)} · left ${agoLabel(profile.lastSeen, now)}`}
        </span>
      </div>
      <ol className="flex flex-col gap-1 px-1.5 pb-1.5">
        {rows.map(({ event, index }) => (
          <EventRow key={event.id} event={event} events={profile.events} index={index} live={live} />
        ))}
        <li className="flex h-9 shrink-0 items-center gap-2.5 px-3">
          <SourceMark source={profile.source} size={14} />
          <span className={cn('min-w-0 flex-1 truncate text-[13px] leading-4 text-foreground/60', profile.source && 'capitalize')}>{sourceLabel}</span>
          <span title={profile.device || profile.deviceGroup}>
            <HugeiconsIcon icon={isMobile(profile) ? SmartPhone01Icon : ComputerIcon} size={14} className="text-foreground/40" />
          </span>
          <span className="shrink-0 text-xs leading-4 text-foreground/60 tabular-nums">{clock(profile.firstSeen)}</span>
        </li>
      </ol>
    </section>
  )
}

function ProfileSkeleton() {
  return (
    <div className={BLEED}>
      <div className="flex shrink-0 flex-col gap-[26px] border-b border-black/5 px-9 py-8 md:w-[340px] md:border-b-0 md:border-r dark:border-white/[0.07]">
        <Skeleton className="size-[72px] rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-[50px] w-full" />
        <Skeleton className="h-[98px] w-full" />
      </div>
      <div className="flex flex-1 flex-col items-center gap-4 bg-canvas px-4 pb-10 pt-7">
        <Skeleton className="h-[26px] w-16 rounded-full" />
        <Skeleton className="h-[260px] w-full max-w-[560px] rounded-xl" />
      </div>
    </div>
  )
}

/**
 * One visitor, full page (Paper → App → Analytics → "Visitor profile · 1440"):
 * who they are on the left, the visit as a timeline on the right. Reached from
 * the Overview live card. Built from the live feed, so it covers the current
 * visit and keeps updating while the visitor is on site.
 */
export default function VisitorProfile() {
  const { visitorKey = '' } = useParams()
  const location = useLocation()
  const { token, propertyId, isAggregateMode, enabledProperties = [] } = useOutletContext<DashboardContext>() ?? {}
  const propertyIds = isAggregateMode
    ? enabledProperties.map((p) => p.propertyId).filter(Boolean).slice(0, 5)
    : propertyId
      ? [propertyId]
      : []
  const { profiles, isLoading } = useLiveVisitorFeed(propertyIds, token)
  const now = useNow(5000)

  const key = decodeURIComponent(visitorKey)
  const profile = profiles.find((p) => p.key === key) ?? null
  const name = visitorName(key)
  const overviewPath = { pathname: location.pathname.replace(/\/visitors\/[^/]+\/?$/, '/traffic'), search: location.search }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast.success('Profile link copied')
    } catch {
      toast.error("Couldn't copy the link")
    }
  }

  const header = (
    <>
      <ShellHeaderCrumb>
        <span aria-current="page" className="truncate text-[13px] font-medium leading-4 text-foreground">
          {name}
        </span>
      </ShellHeaderCrumb>
      {profile && (
        <ShellHeaderActions>
          <button type="button" onClick={copyLink} className={shellControlClass}>
            Share profile
          </button>
        </ShellHeaderActions>
      )}
    </>
  )

  if (isLoading) {
    return (
      <>
        {header}
        <ProfileSkeleton />
      </>
    )
  }

  if (!profile) {
    return (
      <>
        {header}
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <EmptyState
            title="This visit has left the live feed"
            description="Profiles are built from recent live activity. Open a visitor from the live card to see who is on site now."
            action={
              <Link to={overviewPath} className={shellControlClass}>
                Back to Analytics
              </Link>
            }
          />
        </div>
      </>
    )
  }

  const live = isOnSite(profile, now)
  const place = [profile.region, countryName(profile.country)].filter(Boolean).join(', ')
  const os = profile.device?.split(' - ')[0]
  const tech = [profile.browser, os && os !== 'Unknown' ? os : null].filter(Boolean).join(' · ')

  return (
    <>
      {header}
      <div className={BLEED}>
        <aside className="flex shrink-0 flex-col gap-[26px] border-b border-black/5 px-6 py-8 sm:px-9 md:w-[340px] md:border-b-0 md:border-r dark:border-white/[0.07]">
          <Link
            to={overviewPath}
            className="-mb-2 -ml-1 inline-flex w-fit items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium leading-4 text-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
            Back to Analytics
          </Link>
          <VisitorOrb visitorKey={key} className="size-[72px] ring-[3px]" />

          <div className="flex flex-col gap-1.5">
            <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-ui-title">{name}</h1>
            <p className="flex items-center gap-1.5 text-xs leading-4 text-foreground">
              <span className={cn('size-1.5 shrink-0 rounded-full', live ? 'bg-live' : 'bg-foreground/25')} aria-hidden />
              {live ? 'On site now' : `Left ${agoLabel(profile.lastSeen, now)}`} · anonymous visitor
            </p>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {place && <Chip>{place}</Chip>}
            <Chip>
              <SourceMark source={profile.source} />
              <span className="capitalize">{profile.source || 'Direct'}</span>
            </Chip>
            {tech && (
              <Chip>
                <HugeiconsIcon icon={isMobile(profile) ? SmartPhone01Icon : ComputerIcon} size={12} className="text-muted-foreground" />
                {tech}
              </Chip>
            )}
          </div>

          <div className="flex flex-col gap-[18px]">
            <div className="flex">
              <Stat label="Arrived" value={clock(profile.firstSeen)} />
              <Stat label="Time on site" value={formatVisitDuration(visitSeconds(profile, now))} />
            </div>
            <div className="flex">
              <Stat label="Pages viewed" value={String(profile.views)} />
              <Stat label="Clicks and forms" value={`${profile.clicks} · ${profile.forms}`} />
            </div>
          </div>

          <ActivityDots profile={profile} now={now} />

          <p className="rounded-xl bg-canvas p-3 text-xs leading-[18px] text-foreground/60 shadow-[0_0_0_1px_rgb(0_0_0/0.05)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.06)]">
            Names are generated. No personal details are stored about visitors.
          </p>
        </aside>

        <div className="flex flex-1 flex-col items-center bg-canvas px-4 pb-10 pt-7">
          <span className="flex h-[26px] shrink-0 items-center rounded-full bg-card px-3 text-xs font-medium leading-4 text-foreground/60 shadow-[0_0_0_1px_rgb(0_0_0/0.08)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
            {dayLabel(profile.firstSeen, now)}
          </span>
          <span className="h-[18px] w-px shrink-0 bg-foreground/10" aria-hidden />
          <VisitCard profile={profile} now={now} />
          <p className="mt-4 max-w-[560px] text-center text-xs leading-4 text-foreground/50">
            Earlier visits are not linked to this visitor yet. This page shows the current visit from the live feed.
          </p>
        </div>
      </div>
    </>
  )
}
